import { TreeNodeExpanded } from 'business/models/CustomModels';
import ModelFactory from 'business/models/ModelFactory';
import {
  AlertDTO,
  CreateNewTreeNodeDTO,
  DeleteFolderTaskResponseDTO,
  DeleteTreeNodesRequest,
  HitReportDataDTO,
  HitsReportDataRequestDTO,
  InspectionClient,
  InspectionSetGroupDTO,
  PasteResponseDTO,
  ProjectInfoDTO,
  ProjectNodeInfoDTO,
  RenameNodeRequestDTO,
  TreeClient,
  TreeNodeAccessRightDTO,
  TreeNodeDTO,
  TreeNodeListDTO,
  TreeNodeNameDTO,
  UnlockNodeRequestDTO,
} from 'business/models/Models';
import { isNullOrUndefined, getUserToken, getNavigateFunction } from 'utils/Global';
import useRockFetchGet from 'utils/RockFetch';
import BaseService from './BaseService';
import { NavigateFunction } from 'react-router';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { ADVANCED_SEARCH_LOCATION_NODE_TYPES } from 'components/main/tree/SearchBar/AdvancedSearch/utils';

class TreeService extends BaseService {
  private get navigate(): NavigateFunction | ((path: string) => void) {
    return getNavigateFunction() ?? function (path: string) {};
  }

  async isAllTreeNodesExist(treeNodeIds: number[]): Promise<boolean> {
    const tree: TreeClient = ModelFactory(TreeClient, false, true);
    return await tree.isAllTreeNodesExist(treeNodeIds);
  }

  async getTreeNodesService(
    treeNodeId: any,
    validTreeNodesTypes: string[] = null,
    signal?: AbortSignal,
  ) {
    const tree: TreeClient = ModelFactory(TreeClient, false, true, signal);

    if (treeNodeId == null) {
      treeNodeId = -1;
    }

    const root: TreeNodeListDTO = await tree.getTreeNodeByParentId(
      treeNodeId,
      validTreeNodesTypes,
    );

    const data: TreeNodeExpanded[] = [];
    if (isNullOrUndefined(root)) {
      return data;
    }

    for (let i = 0; i < root.treeNodes.length; i++) {
      let id = root.treeNodes[i].id;
      // we need a consistent key where we can find original Id, irrespective of Quickaccess or normal treenode.
      let extra = { originalId: root.treeNodes[i].id } as any;
      if (root.treeNodes[i].extra) {
        extra = root.treeNodes[i].extra;
      }
      const quickAccessMeta = {
        originalId: null,
      };

      extra.quickAccessMeta = quickAccessMeta;

      if (treeNodeId == -2) {
        id = root.treeNodes[i].id * 100000;
        extra.quickAccessMeta.originalId = root.treeNodes[i].id;
      }

      data.push({
        id: id,
        name: root.treeNodes[i].name,
        type: root.treeNodes[i].type,
        datePrepared: root.treeNodes[i]?.datePrepared,
        extra: extra,
        parentId: root.treeNodes[i].parentId,
        accessRightName: root.treeNodes[i].accessRightName,
        hasContainerNode: root.treeNodes[i].hasContainerNode,
        hasChildren: root.treeNodes[i].hasChildren,
        locked: root.treeNodes[i].locked,
        state: {
          expanded: false,
        },
        allowEdits: root.treeNodes[i].allowEdits,
        init: undefined,
        toJSON: undefined,
      });
    }

    return data;
  }

  async getProteinScreenNodesService(id: any, isScreen: boolean) {
    const tree: TreeClient = ModelFactory(TreeClient);

    if (id == null) {
      id = -1;
    }

    const root: TreeNodeListDTO = await tree.getScreenOrProteinTreeNodesByParentId(
      id,
      isScreen,
    );

    const data = [];
    if (isNullOrUndefined(root)) {
      return data;
    }

    for (let i = 0; i < root.treeNodes.length; i++) {
      data.push({
        id: root.treeNodes[i].id,
        name: root.treeNodes[i].name,
        type: root.treeNodes[i].type,
        extra: root.treeNodes[i].extra,
        parentId: root.treeNodes[i].parentId,
        accessRightName: root.treeNodes[i].accessRightName,
        state: {
          expanded: false,
        },
      });
    }

    return data;
  }

  async getSeedNodesService(id: any) {
    const tree: TreeClient = ModelFactory(TreeClient);

    if (id == null) {
      id = -1;
    }

    const root = await tree.getSeedTreeNodesByParentId(id);
    const data = [];

    for (let i = 0; i < root.treeNodes.length; i++) {
      data.push({
        id: root.treeNodes[i].id,
        name: root.treeNodes[i].name,
        type: root.treeNodes[i].type,
        extra: root.treeNodes[i].extra,
        parentId: root.treeNodes[i].parentId,
        state: {
          expanded: false,
        },
      });
    }

    return data;
  }
  async getWizardTreeNodesService(id: any) {
    const tree: TreeClient = ModelFactory(TreeClient);

    if (id == null) {
      id = -1;
    }
    const userToken = await getUserToken();
    if (isNullOrUndefined(userToken)) {
      this.navigate('/login');
      return null;
    }
    const root: TreeNodeListDTO = await tree.getWizardTreeNodeByParentId(id);

    const data = [];
    if (isNullOrUndefined(root)) {
      return data;
    }

    for (let i = 0; i < root.treeNodes.length; i++) {
      if (i == 0 && root.treeNodes[i].type === 'Screens') {
        data.push({
          id: -2,
          name: 'All',
          type: null,
          extra: null,
          accessRightName: root.treeNodes[i].accessRightName,
          hasContainerNode: root.treeNodes[i].hasContainerNode,
          state: {
            expanded: false,
          },
        });
      }
      data.push({
        id: root.treeNodes[i].id,
        name: root.treeNodes[i].name,
        type: root.treeNodes[i].type,
        extra: root.treeNodes[i].extra,
        parentId: root.treeNodes[i].parentId,
        hasContainerNode: root.treeNodes[i].hasContainerNode,
        accessRightName: root.treeNodes[i].accessRightName,
        allowEdits: root.treeNodes[i].allowEdits,
        state: {
          expanded: false,
        },
      });
    }

    return data;
  }
  async getWizardProteinTreeNodesService(id: any, onlyFilterScreens: boolean = false) {
    const tree: TreeClient = ModelFactory(TreeClient, false, true);

    if (id == null) {
      id = -1;
    }
    const userToken = await getUserToken();
    if (isNullOrUndefined(userToken)) {
      this.navigate('/login');
      return null;
    }
    const root: TreeNodeListDTO = await tree.getWizardTreeNodeByParentId(id);

    const disallowedNodeTypes: string[] = [ETreeNodeTypes.Screens];

    if (!onlyFilterScreens) {
      disallowedNodeTypes.push(ETreeNodeTypes.Searches, ETreeNodeTypes.CrystalTrackv4);
    }

    const data = [];
    if (isNullOrUndefined(root)) {
      return data;
    }

    for (let i = 0; i < root.treeNodes.length; i++) {
      if (disallowedNodeTypes.includes(root.treeNodes[i].type)) {
        continue;
      }
      data.push({
        id: root.treeNodes[i].id,
        name: root.treeNodes[i].name,
        type: root.treeNodes[i].type,
        hasContainerNode: root.treeNodes[i].hasContainerNode,
        extra: root.treeNodes[i].extra,
        parentId: root.treeNodes[i].parentId,
        accessRightName: root.treeNodes[i].accessRightName,
        allowEdits: root.treeNodes[i].allowEdits,
        state: {
          expanded: false,
        },
      });
    }

    return data;
  }

  async getUsersTempsImagingSchedules() {
    const tree: TreeClient = ModelFactory(TreeClient);
    return await tree.getUsersTempImagingSchedules();
  }

  async getOptimizationTreeNodesService(id: any) {
    const [rockFetchGet] = useRockFetchGet();

    if (id == null) {
      id = -1;
    }
    const userToken = await getUserToken();
    if (isNullOrUndefined(userToken)) {
      this.navigate('/login');
      return null;
    }
    const root = await rockFetchGet(`/api/Tree/${id}`, 'get', undefined, true);

    const data = [];
    if (isNullOrUndefined(root)) {
      return data;
    }

    for (let i = 0; i < root.treeNodes.length; i++) {
      if (
        root.treeNodes[i].type === 'Projects' ||
        root.treeNodes[i].type === 'Experiment' ||
        root.treeNodes[i].type === 'ProjectsFolder' ||
        root.treeNodes[i].type === 'ProjectFolder' ||
        root.treeNodes[i].type === 'Project'
      ) {
        data.push({
          id: root.treeNodes[i].id,
          name: root.treeNodes[i].name,
          type: root.treeNodes[i].type,
          extra: root.treeNodes[i].extra,
          parentId: root.treeNodes[i].parentId,
          accessRightName: root.treeNodes[i].accessRightName,
          allowEdits: root.treeNodes[i].allowEdits,
          state: {
            expanded: false,
          },
        });
      }
    }

    return data;
  }

  async getOptimizationNewExpTreeNodesService(id: any) {
    const [rockFetchGet] = useRockFetchGet();

    if (id == null) {
      id = -1;
    }
    const userToken = await getUserToken();
    if (isNullOrUndefined(userToken)) {
      this.navigate('/login');
      return null;
    }
    const root = await rockFetchGet(`/api/Tree/${id}`, 'get', undefined, true);

    const data = [];
    if (isNullOrUndefined(root)) {
      return data;
    }

    for (let i = 0; i < root.treeNodes.length; i++) {
      if (
        root.treeNodes[i].type === 'Projects' ||
        root.treeNodes[i].type === 'ProjectsFolder' ||
        root.treeNodes[i].type === 'ProjectFolder' ||
        root.treeNodes[i].type === 'Project'
      ) {
        data.push({
          id: root.treeNodes[i].id,
          name: root.treeNodes[i].name,
          type: root.treeNodes[i].type,
          extra: root.treeNodes[i].extra,
          hasContainerNode: root.treeNodes[i].hasContainerNode,
          parentId: root.treeNodes[i].parentId,
          accessRightName: root.treeNodes[i].accessRightName,
          allowEdits: root.treeNodes[i].allowEdits,
          state: {
            expanded: false,
          },
        });
      }
    }

    return data;
  }

  async getAdvancedSearchTreeNodesService(id?: number) {
    const tree: TreeClient = ModelFactory(TreeClient, false, true);
    const root = (await tree.getTreeNodeByParentId(
      id ?? -1,
      ADVANCED_SEARCH_LOCATION_NODE_TYPES,
    )) as TreeNodeListDTO;

    if (isNullOrUndefined(root)) {
      return [];
    }

    // GetTreeNodeByParentId always returns Quick Access and Recycle Bin if id is -1, so we filter it out again here
    const nodeTypesToFilter: string[] = [
      ETreeNodeTypes.QuickAccess,
      ETreeNodeTypes.RecycleBin,
    ];

    return root.treeNodes
      .filter(
        (treeNode) =>
          !isNullOrUndefined(treeNode.type) && !nodeTypesToFilter.includes(treeNode.type),
      )
      .map((treeNode) => ({
        ...treeNode,
        state: { expanded: false },
      }));
  }

  async createNewTreeNode(tn: CreateNewTreeNodeDTO): Promise<CreateNewTreeNodeDTO> {
    const tree: TreeClient = ModelFactory(TreeClient);
    return tree.createNewTreeNode(tn);
  }

  async getNewUniqueName(
    parentId: number,
    type: string,
    postfix = '',
    signal?: AbortSignal,
  ): Promise<TreeNodeNameDTO> {
    const tree: TreeClient = ModelFactory(TreeClient, false, true, signal);
    return tree.getNewUniqueName(parentId, type, postfix);
  }

  async getDocumentParentNodes(
    id: number,
    type: string,
    includeSelf?: boolean,
  ): Promise<TreeNodeDTO[]> {
    const tree: TreeClient = ModelFactory(TreeClient, false, true);
    return await tree.getDocumentParentNodes(
      id,
      type,
      !isNullOrUndefined(includeSelf) ? includeSelf : false,
    );
  }

  async deleteTreeNode(id: number): Promise<number> {
    const tree: TreeClient = ModelFactory(TreeClient);
    return await tree.deleteTreeNode(id);
  }

  async copyTreeNode(
    nodeIdsToPaste: number[],
    targetId: number,
  ): Promise<PasteResponseDTO> {
    const tree: TreeClient = ModelFactory(TreeClient);
    return await tree.pasteTreeNode(nodeIdsToPaste, targetId);
  }

  async cutPasteTreeNode(
    nodeIdsToPaste: number[],
    targetId: number,
  ): Promise<PasteResponseDTO> {
    const tree: TreeClient = ModelFactory(TreeClient, false, true);
    return await tree.cutPasteTreeNode(nodeIdsToPaste, targetId);
  }

  async getExpProjectInfo(expId: number): Promise<ProjectInfoDTO> {
    const tree: TreeClient = ModelFactory(TreeClient);
    return await tree.getExperimentProjectInfo(expId);
  }
  async deleteInspectionSet(
    inspectionSetGroups: InspectionSetGroupDTO[],
  ): Promise<number> {
    const inspection: InspectionClient = ModelFactory(InspectionClient);
    return await inspection.deleteInspecctionSet(inspectionSetGroups);
  }

  async getHitReportData(
    hitsReportDataRequestDTO: HitsReportDataRequestDTO,
  ): Promise<HitReportDataDTO[]> {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.getTreeNodeHitReportData(hitsReportDataRequestDTO);
  }

  async getParentIdByTreeNodeId(treeNodeId: number) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.getParentIdByTreeNodeId(treeNodeId);
  }

  async getScreenConditionsCount(experimentId: number) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.getScreenConditionsCount(experimentId);
  }

  async getParentNodeByTreeNodeId(treeNodeId: number) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.getParentNodeByTreeNodeId(treeNodeId);
  }
  async getTreeNodeByExpId(expId: number) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.getTreeNodeByExpId(expId);
  }

  async getAbsoluteRootIdByChildId(treeNodeId: number) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.getAbsoluteRootNodeIdFromChildId(treeNodeId);
  }

  async renameNode(treeNodeId: number, newName: string): Promise<AlertDTO> {
    const client: TreeClient = ModelFactory(TreeClient, false, true);
    const renameNodeRequest = new RenameNodeRequestDTO();
    renameNodeRequest.treeNodeId = treeNodeId;
    renameNodeRequest.newName = newName;
    return await client.renameNode(renameNodeRequest);
  }

  async getTreeNodeAccessRights(treeNodeId: number) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.getTreeNodeAccessRights(treeNodeId);
  }

  async updateTreeNodeAccessRights(nodeId: number, payload: TreeNodeAccessRightDTO[]) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.updateTreeNodeAccessRights(nodeId, payload);
  }

  async pinToQuickAccess(treeNodeIds: number[]): Promise<number> {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.addToQuickAccess(treeNodeIds);
  }

  async unpinFromQuickAccess(treeNodeIds: number[]): Promise<number> {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.removeFromQuickAccess(treeNodeIds);
  }

  async UpdateProjectNodeData(node: ProjectNodeInfoDTO) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.updateProjectNodeData(node);
  }

  async GetNodeChildsCount(treeNodeId: number) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.getNodeChildsCount(treeNodeId);
  }

  async GetProjectCrystalData(treeNodeId: number) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.getProjectCrystalData(treeNodeId);
  }

  async GetProjectNodeData(treeNodeId: number) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return await treeClient.getProjectNodeData(treeNodeId);
  }

  async unlockNode(treeNodeId: number) {
    const client: TreeClient = ModelFactory(TreeClient, false, true);
    const unlockNodeRequest = new UnlockNodeRequestDTO();
    unlockNodeRequest.treeNodeId = treeNodeId;
    return await client.unlockNode(unlockNodeRequest);
  }

  async getTreeNodeById(treeNodeId: number) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return treeClient.getTreeNodeByTreeNodeId(treeNodeId);
  }

  async getTreeNodeByType(treeNodeType: string) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return treeClient.getTreeNodesByTreeNodeType(treeNodeType);
  }

  async getTreeNodeAccessRightName(treeNodeId: number) {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return treeClient.getTreeNodeAccessRightName(treeNodeId);
  }

  async getTreeNodePaths(treeNodeIds: number[]): Promise<{ [key: number]: string }> {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return treeClient.getTreeNodePaths(treeNodeIds);
  }

  async getAnalyzeTreeNodes() {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return treeClient.getAnalyzeTreeNodes();
  }

  async IsAnyTreeNodeContainHits(treeNodeIds: number[]): Promise<boolean> {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return treeClient.isAnyTreeNodeContainsHit(treeNodeIds);
  }

  async deleteTreeNodes(
    req: DeleteTreeNodesRequest,
  ): Promise<DeleteFolderTaskResponseDTO[]> {
    const treeClient: TreeClient = ModelFactory(TreeClient, false, true);
    return treeClient.deleteTreeNodes(req);
  }

  async getAllTreeNodesService(
    validTreeNodesTypes: string[] = null,
    signal?: AbortSignal,
    includeScreenConditionsCount = true,
  ) {
    const tree: TreeClient = ModelFactory(TreeClient, false, true, signal);
    const nodes = await tree.getAllTreeNodes(
      validTreeNodesTypes,
      includeScreenConditionsCount,
    );
    return nodes;
  }

  async getDeletedNodeAvailability(req: number[]): Promise<DeleteFolderTaskResponseDTO> {
    const tree: TreeClient = ModelFactory(TreeClient, false, true);
    const response = await tree.getDeletedNodeAvailability(req);
    return response;
  }
}

export { TreeService };
